import { useEffect, useRef, useState } from 'react'

import { cable } from '../../cable.js'
import { INITIAL_PULSE_STATE, fetchSettled, pulsed } from '../utils/pulseState.js'

// How long to coalesce a burst before refetching. Eleven controllers broadcast
// and a paste fires a pulse per row, so this is load protection, not padding.
// Exported because nine components still declare their own copy
// (CashFlowReport, CashFlowTable, the four growth panels, the three event
// tables) — when they migrate onto this hook, converge here rather than
// forking the number a tenth time.
export const REALTIME_DEBOUNCE = 1500

// EVA-300: subscribe to a valuation's pulse, debounce the refetch it triggers,
// and report whether the figures currently on screen are still being
// recalculated. The rules live in utils/pulseState.js, which spec/javascript
// probes; this hook is the React glue — the subscription and the timer.
//
// `onPulse` has two requirements, and both fail SILENTLY if broken:
//
//   * it must be the component's SILENT refetch. It is called with no
//     arguments, so a refetch whose spinner defaults to on replaces the view
//     with a throbber on every upstream edit — the EVA-275 blank-the-grid
//     regression. ValuationSummaryTab wraps its own for exactly this reason.
//   * it must return a promise that resolves once the response has been
//     APPLIED. A non-async refetch makes the await below resolve instantly and
//     collapses `pending` into "the debounce elapsed", which is not what the
//     indicator claims.
//
// It is reached through a ref so the effect keeps `[valuationId]`: with the
// callback in the dependency array every render would tear the subscription
// down and rebuild it, losing the debounce in flight with it.
//
// There is deliberately no `connected` / `disconnected` seam here. A component
// that also renders a ConnectionStatus badge (CashFlowReport) needs one added
// before it can migrate.
export default function useValuationPulse(valuationId, onPulse) {
  const [pending, setPending] = useState(false)
  const stateRef = useRef(INITIAL_PULSE_STATE)
  const onPulseRef = useRef(onPulse)

  useEffect(() => { onPulseRef.current = onPulse }, [onPulse])

  useEffect(() => {
    // Above the guard, not below it: a valuation going away is as much a
    // change of subject as a valuation being replaced, and resetting only on
    // the truthy path would leave the last one's pending on screen forever.
    // A different valuation is a different conversation — carrying the old
    // one's pending over would leave the new figures reading as provisional
    // until a pulse for THIS valuation happened to arrive. Unreachable while
    // the header mount has no `data-turbo-permanent` and React is torn down
    // per visit, but that is a guarantee two files away; this keeps the
    // dependency array honest on its own.
    stateRef.current = INITIAL_PULSE_STATE
    setPending(false)

    if (!valuationId) return undefined

    let debounceTimer = null

    const apply = (next) => {
      stateRef.current = next
      setPending(next.pending)
    }

    const subscription = cable.subscriptions.create(
      { channel: 'ValuationChannel', valuation_id: valuationId },
      {
        received() {
          // Before the debounce, deliberately: the pause the user complains
          // about is mostly this timer, so waiting it out to say "working"
          // would announce the delay only after it had passed.
          apply(pulsed(stateRef.current))

          if (debounceTimer) clearTimeout(debounceTimer)
          debounceTimer = setTimeout(async () => {
            // Captured before the await, not read back at settle time: see
            // fetchSettled's comment for the overtake this prevents.
            const startedSeq = stateRef.current.seq
            try {
              await onPulseRef.current?.()
            } catch {
              // The component owns its own error surface — both current
              // callers already catch and either keep the previous values or
              // render an alert. Swallowing here only stops an unhandled
              // rejection escaping a timer callback nothing awaits.
            } finally {
              // In `finally` so a refused refetch clears the indicator too:
              // leaving a spinner spinning over unchanged values would promise
              // an update that is not coming.
              apply(fetchSettled(stateRef.current, startedSeq))
            }
          }, REALTIME_DEBOUNCE)
        }
      }
    )

    return () => {
      subscription.unsubscribe()
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [valuationId])

  return pending
}
